// src/components/Dashboard.jsx
//
// Landing page after login. Every number on this page comes from
// GET /dashboard/stats (dashboard_api in app.py), which aggregates the
// real `projects` table (status = 'P' in progress / 'C' completed) and
// Rate_Card -- nothing here is placeholder/demo data. The four stat
// cards (each with its own trailing sparkline), the "by Operating
// Company" bar chart, the monthly submissions trend line, and the
// Completed/In Progress pie chart all read from that one response.
//
// Markup uses the CET-HTML-Code theme's own Bootstrap classes (row /
// col-xl-3 col-md-6 / card / card-body / badge-soft-*) instead of the
// old hand-rolled dash-* classes -- those were only ever styled by
// appLayout.css, which Layout.jsx stopped importing once the app moved
// to the theme's compiled CSS (vendors.min.css + app.min.css, loaded
// globally from public/index.html). Keeping the old classnames here
// left every stat card and chart panel completely unstyled.
//
// Percentage badges only render when the backend could compute a real
// last-30-days-vs-prior-30-days (or year-over-year, for the Rate Card
// count) change -- if there's no prior-period data to compare against,
// the badge is simply omitted rather than showing a made-up number.
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
// FIX: was a bare `axios` import -- this page calls GET /dashboard/stats
// (dashboard_api in app.py), which now requires a JWT like every other
// API route. `api` is the pre-configured instance from apiConfig.js that
// attaches "Authorization: Bearer <token>" automatically; aliasing it
// back to `axios` here means every axios.get/post call below needs no
// other changes.
import { api as axios } from "../config/apiConfig";
import { Line, Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Filler,
  Title,
  Tooltip,
  Legend
} from "chart.js";
import {
  FaClipboardList,
  FaWallet,
  FaChartBar,
  FaDatabase,
  FaArrowUp,
  FaArrowDown
} from "react-icons/fa";
import { API_ORIGIN } from "../config/apiConfig";
import DashboardExecutiveSummary from "./DashboardExecutiveSummary";
import MasterComingSoon from "./MasterComingSoon";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, ArcElement, Filler, Title, Tooltip, Legend);

const API_BASE = API_ORIGIN;

// ---- palette (validated categorical + status steps -- see dataviz skill) ----
const BLUE = "#2a78d6";
const ORANGE = "#eb6834";
const AQUA = "#1baf7a";
const YELLOW = "#eda100";
const MAGENTA = "#e87ba4";
const GREEN_ROOT = "#008300";
const VIOLET = "#4a3aa7";
const RED = "#e34948";
const CATEGORICAL = [BLUE, ORANGE, AQUA, YELLOW, MAGENTA, GREEN_ROOT, VIOLET, RED];
const STATUS_GOOD = "#0ca30c";
const MUTED = "#898781";
const GRIDLINE = "#e1e0d9";

// Icon + accent color per stat card -- mirrors the theme's own
// dashboard.html demo cards (leaf/success, wallet/info, chart/warning,
// hand-money/secondary) so this page reads as part of the same theme.
const STAT_CARDS = [
  {
    key: "submitted",
    label: "Projects Submitted For Cost Estimation",
    icon: FaClipboardList,
    textClass: "text-success",
    shareKey: null
  },
  {
    key: "completed",
    label: "Cost Estimation Completed",
    icon: FaWallet,
    textClass: "text-info",
    shareKey: "completed_pct"
  },
  {
    key: "in_progress",
    label: "Cost Estimation In Progress",
    icon: FaChartBar,
    textClass: "text-warning",
    shareKey: "in_progress_pct"
  },
  {
    key: "rate_card_items",
    label: "Number Of Items In Cost Database",
    icon: FaDatabase,
    textClass: "text-secondary",
    shareKey: null
  }
];

// Small pill showing a stat's % share of total submitted projects --
// separate from DeltaBadge (period-over-period change): this is a
// point-in-time composition figure, e.g. "42% of submitted".
function ShareBadge({ pct }) {
  if (pct === null || pct === undefined) return null;
  return <span className="badge fs-12 badge-soft-secondary ms-1">{pct}% of submitted</span>;
}

function DeltaBadge({ pct }) {
  if (pct === null || pct === undefined) return null;
  const up = pct >= 0;
  return (
    <span className={`badge fs-12 ${up ? "badge-soft-success" : "badge-soft-danger"}`}>
      {up ? <FaArrowUp className="align-middle" /> : <FaArrowDown className="align-middle" />} {Math.abs(pct)}%
    </span>
  );
}

// Trailing sparkline for a stat card -- sits at the bottom of the card
// body, same spot the theme's demo cards fill with a static chart PNG.
function Sparkline({ values }) {
  const hasData = Array.isArray(values) && values.length > 0 && values.some((v) => v > 0);
  const data = {
    labels: (values || []).map((_, i) => i),
    datasets: [
      {
        data: values || [],
        borderColor: BLUE,
        borderWidth: 2,
        fill: true,
        backgroundColor: "rgba(42,120,214,0.14)",
        tension: 0.4,
        pointRadius: 0
      }
    ]
  };
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { enabled: false } },
    scales: {
      x: { display: false },
      // beginAtZero: without this, each card's y-axis auto-scaled to
      // its OWN min/max, so a card whose values ranged e.g. 20-35 had
      // its dip drawn near the middle while a card ranging 0-10 had
      // its dip drawn near the bottom -- same 50px box, visually
      // inconsistent "heights". Anchoring every sparkline to a shared
      // zero baseline makes all four read at a consistent scale.
      y: { display: false, beginAtZero: true }
    },
    elements: { line: { borderCapStyle: "round" } }
  };
  if (!hasData) {
    // Same fixed 50px footprint as the real chart below (not just a
    // bare text line) -- otherwise a card with no sparkline history
    // (e.g. "Number Of Items In Cost Database", which has no time
    // series) reserved less vertical space than the other three cards,
    // making the row of four look mismatched in height even though
    // beginAtZero already fixed the chart-to-chart scaling.
    return (
      <div className="mt-2 d-flex align-items-center text-muted fs-12" style={{ height: 50 }}>
        Not enough history yet
      </div>
    );
  }
  return (
    <div className="mt-2" style={{ height: 50 }}>
      <Line data={data} options={options} />
    </div>
  );
}

// Draws the numeric value above each bar -- same pattern already used
// on the Step 5 cost-breakdown chart, for a direct value label on every
// bar (the palette's aqua/yellow/magenta slots need this "relief" since
// they sit below 3:1 contrast on a light surface).
const barValueLabelPlugin = {
  id: "dashBarValueLabel",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    chart.data.datasets.forEach((dataset, dsIndex) => {
      const meta = chart.getDatasetMeta(dsIndex);
      meta.data.forEach((bar, index) => {
        const value = Number(dataset.data[index] || 0);
        if (!value) return;
        ctx.save();
        ctx.fillStyle = "#0b0b0b";
        ctx.font = "600 11px sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(String(value), bar.x, bar.y - 6);
        ctx.restore();
      });
    });
  }
};

// Draws each pie slice's percentage directly on the slice.
const pieValueLabelPlugin = {
  id: "dashPieValueLabel",
  afterDatasetsDraw(chart) {
    const { ctx } = chart;
    const meta = chart.getDatasetMeta(0);
    const dataset = chart.data.datasets[0];
    const total = dataset.data.reduce((s, v) => s + Number(v || 0), 0);
    if (!total) return;
    meta.data.forEach((arc, index) => {
      const value = Number(dataset.data[index] || 0);
      if (!value) return;
      const pct = ((value / total) * 100).toFixed(1);
      const pos = arc.tooltipPosition();
      ctx.save();
      ctx.fillStyle = "#ffffff";
      ctx.font = "700 12px sans-serif";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(`${pct}%`, pos.x, pos.y);
      ctx.restore();
    });
  }
};

export default function Dashboard() {
  // Every wizard ReadOnly step page's "Back to Dashboard" button
  // (Step1ProjectInfoReadOnly.jsx etc.) navigates here with
  // navigate(ROUTES.DASHBOARD, { state: { tab: "executive" } }) so the
  // admin lands back on the Executive Summary tab they came from,
  // instead of always resetting to "Overall". Any other link to this
  // page (SideMenu, Header, etc.) doesn't pass that state, so this
  // still defaults to "overall" exactly as before.
  const location = useLocation();

  // "Overall" (current, unfiltered portfolio view) vs "Executive Summary"
  // (OpCo -> Project ID drill-down) -- see DashboardExecutiveSummary.jsx
  // for the second tab's content.
  const [activeTab, setActiveTab] = useState(location.state?.tab === "executive" ? "executive" : "overall");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Per request, the Executive Summary tab's OpCo / Project Name filter
  // dropdowns move up here so they can render on the right side of the
  // Overall/Executive Summary tab-pill row below, instead of their own
  // separate card underneath it. This is genuinely lifted state (not just
  // moved rendering) -- DashboardExecutiveSummary.jsx used to own all of
  // this itself; it now receives the selection + list props instead and
  // stays purely presentational for the chart/table/tile part. See that
  // file's props for exactly what's still local there (just the
  // once-a-project-is-picked summary fetch).
  const [opcoList, setOpcoList] = useState([]);
  const [opcoLoading, setOpcoLoading] = useState(true);
  const [opcoError, setOpcoError] = useState(null);
  const [selectedOpco, setSelectedOpco] = useState("");

  const [projectList, setProjectList] = useState([]);
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);
  const [selectedProjectId, setSelectedProjectId] = useState("");

  // See handleSelectProject below (moved up from
  // DashboardExecutiveSummary.jsx's old handleSummaryRowClick) for why
  // this exists -- a table-row click needs to change OpCo AND Project ID
  // together, but changing selectedOpco resets the Project ID list/pick
  // via the effect below, so the target project id is stashed here and
  // applied once that OpCo's project list has (re)loaded.
  const [pendingProjectId, setPendingProjectId] = useState("");

  // MATARAT is the parent holding company over every other OpCo in this
  // dropdown (DAMMAM, JEDCO, RAC, Cluster2, ...) -- it isn't itself an
  // operating company that runs its own projects, so no project row is
  // ever tagged with OpCo_ID = MATARAT's id. Per request, picking it
  // means "show the whole group" rather than "filter down to (zero)
  // MATARAT-only projects": both the Project Name dropdown below and the
  // "All Cost Estimation Details" table in DashboardExecutiveSummary.jsx
  // fall back to every company's projects when this is selected, instead
  // of coming back empty like any other OpCo with no completed projects
  // legitimately would.
  const selectedOpcoName = opcoList.find((o) => o.OpCo_ID === selectedOpco)?.OpCo_Name || "";
  const isMataratSelected = selectedOpcoName.trim().toUpperCase() === "MATARAT";

  // OpCo dropdown -- loaded once.
  useEffect(() => {
    axios
      .get(`${API_BASE}/opco`)
      .then((res) => setOpcoList(res.data?.data || []))
      .catch(() => setOpcoError("Failed to load Operating Companies."))
      .finally(() => setOpcoLoading(false));
  }, []);

  // Project ID dropdown -- dependent on the selected OpCo. An executive
  // summary (Budget vs Estimation actuals) only makes sense for a project
  // whose cost estimation is actually finished, so this only lists
  // Completed (status "C") projects -- an in-progress project's
  // "estimated" figures aren't final yet.
  useEffect(() => {
    setSelectedProjectId("");
    setProjectList([]);
    setProjectError(null);
    if (!selectedOpco) return;
    setProjectLoading(true);
    // MATARAT (see isMataratSelected above) has no projects of its own --
    // drop the opco_id filter entirely so this lists every company's
    // completed projects instead of coming back empty.
    const url = isMataratSelected
      ? `${API_BASE}/projects?pageSize=100`
      : `${API_BASE}/projects?opco_id=${encodeURIComponent(selectedOpco)}&pageSize=100`;
    axios
      .get(url)
      .then((res) => {
        const all = res.data?.data || [];
        const completed = all.filter((p) => (p.status || "").trim().toUpperCase() === "C");
        setProjectList(completed);
        // If this OpCo load was triggered by a table-row click (see
        // handleSelectProject below), and the clicked project is actually
        // in this OpCo's completed list, select it now that the
        // dropdown's options are populated.
        setPendingProjectId((pending) => {
          if (pending && completed.some((p) => p.projectid === pending)) {
            setSelectedProjectId(pending);
          }
          return "";
        });
      })
      .catch(() => setProjectError("Failed to load projects for this Operating Company."))
      .finally(() => setProjectLoading(false));
  }, [selectedOpco, isMataratSelected]);

  // Passed down to DashboardExecutiveSummary as onRowSelectProject --
  // resolves a clicked row's OpCo name to an OpCo_ID (the dropdowns above
  // only know projects by OpCo_ID) and kicks off the same OpCo -> Project
  // ID cascade the dropdowns use, via pendingProjectId above. Falls back
  // to selecting the project directly (skipping the OpCo dropdown sync)
  // if the OpCo can't be matched, so the graph still shows either way.
  const handleSelectProject = (row) => {
    const opco = opcoList.find(
      (o) => (o.OpCo_Name || "").trim().toLowerCase() === (row.opco_name || "").trim().toLowerCase()
    );
    if (opco && opco.OpCo_ID !== selectedOpco) {
      setPendingProjectId(row.projectid);
      setSelectedOpco(opco.OpCo_ID);
    } else {
      setSelectedProjectId(row.projectid);
    }
  };

  useEffect(() => {
    axios
      .get(`${API_BASE}/dashboard/stats`)
      .then((res) => {
        if (res.data && res.data.success) {
          setStats(res.data.data);
        } else {
          setError(res.data?.error || "Failed to load dashboard data.");
        }
      })
      .catch((err) => {
        console.error("Failed to load dashboard stats:", err);
        setError("Failed to load dashboard data.");
      })
      .finally(() => setLoading(false));
  }, []);

  // Everything below (byOpco..pieOptions) is only meaningful once `stats`
  // has loaded, so it's built inside this conditional and assigned to
  // `overallContent` -- the tabs themselves (and the Executive Summary
  // tab, which has its own independent data source) render unconditionally
  // below, regardless of the Overall tab's loading/error state.
  let overallContent;

  if (loading) {
    overallContent = (
      <div className="card">
        <div className="card-body text-center py-5">
          <p className="text-muted mb-0">Loading dashboard...</p>
        </div>
      </div>
    );
  } else if (error || !stats) {
    overallContent = (
      <div className="card">
        <div className="card-body">
          <div className="alert alert-danger mb-0">{error || "No dashboard data available."}</div>
        </div>
      </div>
    );
  } else {
  const byOpco = stats.by_opco || [];
  const barData = {
    labels: byOpco.map((r) => r.opco_name),
    datasets: [
      {
        label: "Projects",
        data: byOpco.map((r) => r.count),
        backgroundColor: byOpco.map((_, i) => CATEGORICAL[i % CATEGORICAL.length]),
        borderRadius: 4,
        maxBarThickness: 26,
        categoryPercentage: 0.7,
        barPercentage: 0.9
      }
    ]
  };
  const barOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 10 }, color: MUTED, maxRotation: 45, minRotation: 0 } },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { size: 11 }, color: MUTED },
        grid: { color: GRIDLINE }
      }
    }
  };

  const monthly = stats.monthly_submissions || [];
  const lineData = {
    labels: monthly.map((m) => m.label),
    datasets: [
      {
        label: "Submissions",
        data: monthly.map((m) => m.value),
        borderColor: BLUE,
        backgroundColor: "rgba(42,120,214,0.10)",
        fill: true,
        tension: 0.35,
        pointRadius: 3,
        pointBackgroundColor: BLUE,
        borderWidth: 2
      }
    ]
  };
  const lineOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: {
      x: { grid: { display: false }, ticks: { font: { size: 11 }, color: MUTED } },
      y: {
        beginAtZero: true,
        ticks: { precision: 0, font: { size: 11 }, color: MUTED },
        grid: { color: GRIDLINE }
      }
    }
  };

  const statusBreakdown = stats.status_breakdown || [];
  const statusColorFor = (code) => {
    if (code === "C") return STATUS_GOOD;
    if (code === "P") return BLUE;
    return MUTED;
  };
  const pieData = {
    labels: statusBreakdown.map((s) => s.label),
    datasets: [
      {
        data: statusBreakdown.map((s) => s.count),
        backgroundColor: statusBreakdown.map((s) => statusColorFor(s.status)),
        borderColor: "#fff",
        borderWidth: 2
      }
    ]
  };
  const pieOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 12 }, color: "#374151" } }
    }
  };

  overallContent = (
    <>
      <div className="row">
        {STAT_CARDS.map((card) => {
          const Icon = card.icon;
          const value = stats.totals?.[card.key] ?? 0;
          const pct = stats.deltas?.[`${card.key}_pct`];
          const sharePct = card.shareKey ? stats.status_share?.[card.shareKey] : null;
          const sparkline = stats.sparklines?.[card.key] || [];
          return (
            <div className="col-xl-3 col-md-6" key={card.key}>
              <div className="card">
                <div className="card-body">
                  <Icon className={`fs-36 ${card.textClass}`} />
                  <h3 className="fw-bold mt-3 mb-1">{value.toLocaleString()}</h3>
                  <p className="text-muted">{card.label}</p>
                  <DeltaBadge pct={pct} />
                  <ShareBadge pct={sharePct} />
                  <Sparkline values={sparkline} />
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="row">
        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Projects by Operating Company</h5>
              <div style={{ height: 280 }}>
                {byOpco.length === 0 ? (
                  <div className="text-muted text-center py-5">No projects yet.</div>
                ) : (
                  <Bar data={barData} options={barOptions} plugins={[barValueLabelPlugin]} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Cost Estimation Submissions (last 7 months)</h5>
              <div style={{ height: 280 }}>
                <Line data={lineData} options={lineOptions} />
              </div>
            </div>
          </div>
        </div>

        <div className="col-md-4">
          <div className="card">
            <div className="card-body">
              <h5 className="card-title mb-3">Project Status Breakdown</h5>
              <div style={{ height: 280 }}>
                {statusBreakdown.length === 0 ? (
                  <div className="text-muted text-center py-5">No projects yet.</div>
                ) : (
                  <Pie data={pieData} options={pieOptions} plugins={[pieValueLabelPlugin]} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
  }

  return (
    <>
      {/* Every other page in the app (Step1ProjectInfo, RateCard, ProjectList,
          RoleManagement, etc.) opens with a "page-title-head" block before its
          main content -- that element's own min-height (60px, from app.min.css)
          plus its bottom spacing is what gives those pages breathing room under
          the sticky top header. Dashboard.jsx never had one, so its stat-cards
          row sat flush against the header with nothing pushing it down -- this
          adds the same heading block so Dashboard matches the rest of the app. */}
      <div className="page-title-head d-flex align-items-center mb-0">
        <div className="flex-grow-1">
          {/* Per request, this is now a breadcrumb trail ("Cost
              Estimation / {page}"), same pattern and color (#02bc9c,
              16px) as the Cost Estimation wizard's Step1-7 breadcrumbs
              -- replaces the earlier #788CA5 bordered/uppercase title
              treatment. (A pill/chip-style variant was tried here and
              then reverted back to this plain style, per follow-up
              request, to stay consistent with every other page.) */}
          <nav aria-label="breadcrumb" className="d-inline-block">
            <span style={{ fontSize: 16, color: "#02bc9c" }}>Cost Estimation</span>
            <span style={{ fontSize: 16, color: "#02bc9c" }} className="mx-2">/</span>
            <span className="fw-semibold" style={{ fontSize: 16, color: "#02bc9c" }}>
              Dashboard
            </span>
          </nav>
        </div>
      </div>

      {/* Same nav-pills tab pattern as the theme's own Notifications page
          (page-title-head, then a pill-tab row, then the active tab's
          content) -- controlled by React state rather than Bootstrap's
          data-bs-toggle JS, same approach already used for UsersList.jsx's
          role tabs. */}
      <div className="align-items-center mb-3 row g-2">
        <div className="d-flex col-md-6 justify-content-start">
          <ul className="bg-light border flex-nowrap flex-sm-wrap nav nav-pills overflow-auto p-1 rounded" role="tablist">
            <li className="flex-shrink-0 nav-item" role="presentation">
              <button
                type="button"
                className={`px-3 fw-semibold nav-link px-md-4${activeTab === "overall" ? " active" : ""}`}
                onClick={() => setActiveTab("overall")}
              >
                Overall
              </button>
            </li>
            <li className="flex-shrink-0 nav-item" role="presentation">
              <button
                type="button"
                className={`px-3 fw-semibold nav-link px-md-4${activeTab === "executive" ? " active" : ""}`}
                onClick={() => setActiveTab("executive")}
              >
                Executive Summary
              </button>
            </li>
          </ul>
        </div>

        {/* Per request, the Executive Summary tab's OpCo / Project Name
            filters now sit on the right side of this same tab-pill row
            (this col-md-6 was empty before) instead of their own filter
            card underneath -- only shown while that tab is actually
            active, since they don't apply to Overall. */}
        {activeTab === "executive" && (
          <div className="col-md-6">
            <div className="row g-2 justify-content-md-end">
              <div className="col-6 col-lg-5">
                <select
                  className="form-select form-select-sm"
                  value={selectedOpco}
                  onChange={(e) => setSelectedOpco(e.target.value)}
                  disabled={opcoLoading}
                  aria-label="Company"
                >
                  {/* Per request, relabeled from "OpCo" to "Company" --
                      matches EstimationSummary.jsx's earlier "OpCo" ->
                      "Company Name" tile rename; still the same OpCo_ID
                      values/options underneath. */}
                  <option value="">{opcoLoading ? "Loading..." : "Select Company"}</option>
                  {opcoList.map((o) => (
                    <option key={o.OpCo_ID} value={o.OpCo_ID}>
                      {o.OpCo_Name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="col-6 col-lg-5">
                <select
                  className="form-select form-select-sm"
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  disabled={!selectedOpco || projectLoading}
                  aria-label="Project Name"
                >
                  <option value="">
                    {!selectedOpco ? "Select a Company first" : projectLoading ? "Loading..." : "Select Project Name"}
                  </option>
                  {projectList.map((p) => (
                    <option key={p.id} value={p.projectid}>
                      {p.project_name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {opcoError && <div className="text-danger fs-xxs mt-1 text-md-end">{opcoError}</div>}
            {projectError && <div className="text-danger fs-xxs mt-1 text-md-end">{projectError}</div>}
          </div>
        )}
      </div>

      {/* Per request, the "Overall" tab shows the same "Coming Soon" page
          used on the Master menu item (reusing the exact same component)
          instead of overallContent (the stat cards/charts below) --
          "Executive Summary" is untouched and still fully functional.
          overallContent itself is left computed above rather than
          deleted, so switching back later is just this one line. */}
      {activeTab === "overall" ? (
        <MasterComingSoon />
      ) : (
        <DashboardExecutiveSummary
          selectedOpco={selectedOpco}
          // Per request, picking an OpCo up in the tab-pill row filters
          // the "All Cost Estimation Details" landing table below to
          // that OpCo's own projects, resolved by name (see opco_name on
          // each row, from GET /projects) since that table doesn't know
          // OpCo_ID. MATARAT is the exception (see isMataratSelected
          // above) -- it's the parent holding company, not an operating
          // company with projects of its own, so it's passed down as ""
          // (no filter) so the table shows every company's projects
          // instead of coming back empty.
          selectedOpcoName={isMataratSelected ? "" : selectedOpcoName}
          selectedProjectId={selectedProjectId}
          projectList={projectList}
          projectLoading={projectLoading}
          onSelectRow={handleSelectProject}
        />
      )}
    </>
  );
}